import { Box, Flex, Text } from '@chakra-ui/react';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Banner, ButtonText } from '@morressier/alembic';
import { recommendationBadges } from '../Review/Badges';
import { useAdjudicationData } from 'src/contexts/AdjudicationDataProvider';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import ReviewTableScore from '../ReviewTableScore';
import { FileListDisplay } from '../Submission/GenericFieldComponents/FileList/FileListDisplay';
import { IAdjudicationReview } from 'src/pages/MyTask/Adjudications/adjudicationType';
import { formatDate } from 'src/utils/date';
import { Overlay } from '../Overlay';
import { allKeysHaveValues } from 'src/pages/MyTask/Assessments/hooks';

type IReviewDetailsProps = {
  data: IAdjudicationReview;
  isOpen?: boolean;
  onClose: () => void;
  index: number;
};

const ReviewDetailsModal = ({ data, onClose, index }: IReviewDetailsProps) => {
  const { revisionFormSchema: schemaData } = useAdjudicationData();

  const formFieldData = schemaData?.documentSchema!;

  const reviewFormFields = new ReviewFormFields(formFieldData);

  const submissionScore = data?.firstAssessment?.submissionScores;
  const isSubmissionScoresData = allKeysHaveValues(submissionScore);

  const config = {
    significance: reviewFormFields.getFieldConfig('Significance'),
    originality: reviewFormFields.getFieldConfig('Originality'),
    rigour: reviewFormFields.getFieldConfig('Rigour'),
    clarity: reviewFormFields.getFieldConfig('Clarity'),
    impact: reviewFormFields.getFieldConfig('Impact')
  };

  const reviewDate = data?.workflowData.states?.find(
    state => state.name === 'initial'
  )?.completedWith?.date;

  const coReview = data?.coReview;
  const isCoReviewed = coReview?.status === 'accepted';
  const coReviewerObj = coReview?.coReviewer;
  const coReviewer =
    coReviewerObj?.firstName && coReviewerObj?.lastName
      ? `${coReviewerObj.firstName} ${coReviewerObj.lastName}`
      : coReviewerObj?.email;
  const reviewedBy =
    `by Reviewer ${index + 1}` +
    (reviewDate ? ` on ${formatDate(new Date(reviewDate))}` : '');

  const closeButton = onClose ? (
    <Flex marginBottom={3} alignItems="center" flexDirection="row">
      <ButtonText
        onClick={onClose}
        leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
      >
        Back to Reviews
      </ButtonText>
    </Flex>
  ) : null;

  return (
    <Overlay height="100%">
      <Flex
        width="100vw"
        flexDirection="column"
        backgroundColor="surfaceSecondaryFill"
        minH="100vh"
      >
        <Flex
          flexDirection="column"
          gap={3}
          padding={10}
          width={'60%'}
          marginX={'auto'}
        >
          {closeButton}
          <Text textStyle={'headingSection'}>Adjudicate this Review</Text>
          <Text textStyle="textBody">{reviewedBy}</Text>
          {isCoReviewed && coReviewer && (
            <Banner
              variant="attention"
              heading="Co-Review Notice"
              content={`This review was submitted with a Co-Reviewer. Both Reviewers will be notified of any changes or scores, and this Review will be credited to both accounts.`}
            />
          )}
          <Accordion
            chakraAccordionProps={{
              allowMultiple: true,
              defaultIndex: [0, 1, 2],
              width: '100%',
              background: 'surfacePrimaryFill'
            }}
            items={[
              {
                title: (
                  <Flex direction={'column'}>
                    <Text textStyle={'headingItem'}>For Editors</Text>
                    <Text textStyle={'textBody'}>
                      Review the scores, feedback and recommendations sent by
                      the reviewer.
                    </Text>
                  </Flex>
                ),
                content: (
                  <Flex direction="column" gap={6}>
                    <Box>
                      <Text textStyle={'headingItem'}>Recommendation</Text>
                      {data?.firstAssessment?.recommendation &&
                        recommendationBadges[
                          data?.firstAssessment?.recommendation
                        ]}
                    </Box>
                    {isSubmissionScoresData && submissionScore && config && (
                      <Box flexDirection="column">
                        <Text textStyle="headingItem">Submission Scores</Text>
                        <Box marginX={2} marginTop={4}>
                          <ReviewTableScore
                            submissionScores={submissionScore}
                            reviewFieldConfig={config}
                          />
                        </Box>
                      </Box>
                    )}
                  </Flex>
                )
              }
            ]}
          />
          <Accordion
            chakraAccordionProps={{
              allowMultiple: true,
              width: '100%',
              background: 'surfacePrimaryFill'
            }}
            items={[
              {
                title: (
                  <Flex direction={'column'}>
                    <Text textStyle={'headingItem'}>For Authors</Text>
                    <Text textStyle={'textBody'}>
                      Review the feedback left by the Reviewers for Authors.
                    </Text>
                  </Flex>
                ),
                content: (
                  <Flex direction="column" gap={6}>
                    {data?.firstAssessment.feedbackForAuthors?.moderated && (
                      <Box marginTop={3}>
                        <Banner
                          heading="For your attention"
                          content="The Editors have moderated the reviewer's original feedback. Review both versions to ensure a thorough assessment."
                          variant="attention"
                        />
                      </Box>
                    )}

                    {data?.firstAssessment?.feedbackForAuthors?.original && (
                      <Box>
                        <Text textStyle="headingItem">
                          Feedback for Authors
                        </Text>
                        <Text textStyle="textBody">
                          {data?.firstAssessment.feedbackForAuthors?.original ||
                            'No data available'}
                        </Text>
                      </Box>
                    )}
                    {data?.firstAssessment?.feedbackForAuthors?.moderated && (
                      <Box>
                        <Text textStyle="headingItem">
                          Moderated Feedback for Authors
                        </Text>
                        <Text textStyle="textBody">
                          {data?.firstAssessment.feedbackForAuthors
                            ?.moderated || 'No data available'}
                        </Text>
                      </Box>
                    )}
                    <Box>
                      <Text textStyle={'headingItem'}>Review Attachments</Text>
                      <Flex flexDirection="column" gap={4}>
                        {data?.firstAssessment?.reviewerAttachments &&
                        data?.firstAssessment?.reviewerAttachments?.length >
                          0 ? (
                          <FileListDisplay
                            files={data.firstAssessment?.reviewerAttachments}
                          />
                        ) : (
                          'Reviewer Attachments are not available'
                        )}
                      </Flex>
                    </Box>
                  </Flex>
                )
              }
            ]}
          />
        </Flex>
      </Flex>
    </Overlay>
  );
};

export default ReviewDetailsModal;
